import { useState } from "react";
import { Link } from "react-router-dom";
import { FolderOpen, Lock, RefreshCw, Repeat, Repeat1, Shuffle } from "lucide-react";
import { sub } from "@/components/music/LibraryTab";
import { FavButton } from "@/components/music/FavButton";
import { Cover } from "@/components/music/Cover";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useMusic } from "@/lib/music/MusicProvider";
import { Seek } from "@/components/music/MiniBar";
import { LibraryTab } from "@/components/music/LibraryTab";
import { PlaylistsTab } from "@/components/music/PlaylistsTab";
import { MoodTab } from "@/components/music/MoodTab";
import { SprintTab } from "@/components/music/SprintTab";

export const isPaid = (user) => !!(user?.plan?.pro || user?.plan?.features?.length);

export const Connect = ({ m }) => (
  <div className="space-y-3 py-2 text-center" data-testid="music-connect">
    <p className="text-xs leading-relaxed text-[#A8A090]">Play music from your own computer while you write. Nothing is uploaded; files stay on this device.</p>
    {m.status === "needs-permission" ? <Button size="sm" onClick={m.reconnect} data-testid="music-reconnect"><RefreshCw className="mr-1 h-3.5 w-3.5" />Reconnect “{m.dirName}”</Button>
      : m.canRemember ? <Button size="sm" onClick={m.connect} disabled={m.status === "scanning"} data-testid="music-choose-folder"><FolderOpen className="mr-1 h-3.5 w-3.5" />{m.status === "scanning" ? "Reading folder…" : "Choose music folder"}</Button>
      : <label className="inline-flex cursor-pointer items-center gap-1 rounded-md bg-[#D97706] px-3 py-1.5 text-xs font-medium text-[#0B0D10]" data-testid="music-pick-files-label">
          <FolderOpen className="h-3.5 w-3.5" />Choose music folder
          <input type="file" multiple webkitdirectory="" className="hidden" onChange={(e) => e.target.files?.length && m.pickFiles(e.target.files)} data-testid="music-pick-files" />
        </label>}
    {!m.canRemember && <p className="text-[10px] text-[#7A7468]">This browser can't remember folders, so you'll pick it each visit. Chrome or Edge remember it.</p>}
  </div>
);

const Controls = ({ m }) => {
  const R = m.prefs.repeat === "one" ? Repeat1 : Repeat;
  const tone = (on) => (on ? "text-[#F59E0B]" : "text-[#7A7468] hover:text-[#F3EBDD]");
  return (
    <div className="space-y-1.5 border-b border-[#2D3442] pb-2.5">
      <div className="flex items-center gap-2">
        {m.current && <Cover src={m.cover} size="h-14 w-14" testid="music-now-cover" />}
        <div className="min-w-0 flex-1" data-testid="music-now-playing">
          {m.current ? <>
            <div className="truncate text-xs text-[#F3EBDD]" data-testid="music-now-title">{m.current.title}</div>
            {m.current.artist && <div className="truncate text-[10px] text-[#D6CCBC]" data-testid="music-now-artist">{m.current.artist}</div>}
            {sub({ ...m.current, artist: null }) && <div className="truncate text-[10px] text-[#7A7468]" data-testid="music-now-details">{sub({ ...m.current, artist: null })}</div>}
          </> : <span className="text-xs text-[#7A7468]">Nothing playing</span>}
        </div>
        {m.current && <FavButton m={m} testid="music-fav-toggle" />}
        <button onClick={() => m.setShuffle(!m.prefs.shuffle)} title="Shuffle" className={tone(m.prefs.shuffle)} data-testid="music-shuffle"><Shuffle className="h-3.5 w-3.5" /></button>
        <button onClick={m.cycleRepeat} title={`Repeat: ${m.prefs.repeat}`} className={tone(m.prefs.repeat !== "off")} data-testid="music-repeat"><R className="h-3.5 w-3.5" /></button>
        <input type="range" min={0} max={1} step={0.05} value={m.prefs.volume} onChange={(e) => m.setPrefs((p) => ({ ...p, volume: Number(e.target.value) }))} className="h-1 w-16 accent-[#F59E0B]" aria-label="Volume" data-testid="music-volume" />
      </div>
      {m.current && <Seek audio={m.audio} testid="music-widget-seek" />}
    </div>
  );
};

const TABS = [["library", "Library"], ["playlists", "Playlists"], ["mood", "Mood mix"], ["sprint", "Sprint"]];

export const MusicWidget = ({ sid, getSceneText }) => {
  const { user } = useAuth();
  const m = useMusic();
  const [tab, setTab] = useState("library");
  if (!isPaid(user)) return (
    <div className="space-y-2 py-4 text-center" data-testid="music-locked">
      <Lock className="mx-auto h-5 w-5 text-[#F59E0B]" />
      <p className="text-xs text-[#A8A090]">The music player is free with any paid plan.</p>
      <Link to="/pricing" className="text-xs text-[#F59E0B] hover:underline" data-testid="music-upgrade-link">See plans</Link>
    </div>
  );
  if (!["ready", "session"].includes(m.status)) return <Connect m={m} />;
  return (
    <div className="space-y-2.5" data-testid="music-widget">
      <Controls m={m} />
      <div className="flex gap-1">
        {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`rounded-full px-2.5 py-0.5 text-[11px] transition-colors ${tab === k ? "bg-[#D97706] text-[#0B0D10]" : "text-[#A8A090] hover:bg-[#1A202C]"}`} data-testid={`music-tab-${k}`}>{l}</button>)}
      </div>
      {tab === "library" && <LibraryTab m={m} />}
      {tab === "playlists" && <PlaylistsTab m={m} sid={sid} />}
      {tab === "mood" && <MoodTab m={m} getSceneText={getSceneText} />}
      {tab === "sprint" && <SprintTab m={m} />}
    </div>
  );
};
